import { ACL_TYPE } from './aclType';
import type { ConstValues, NumberString } from './helpers';
import type { Market } from './market';
import type { ObjectType } from './objectTypes';
import type { SellerJsonType } from './sellerType';

export type SellerJsonOverride = {
  override_seller_type: SellerJsonType;
  override_domain: string | null;
  override_name: string | null;
};

export const AccountType = {
  ADVERTISER: 'account.advertiser',
  AD_AGENCY: 'account.agency',
  AD_NETWORK: 'account.network',
  PARTNER: 'account.partner',
  PUBLISHER: 'account.publisher',
  SUB_ACCOUNT: 'account.sub_account',
} as const;
export type AccountType = ConstValues<typeof AccountType>;

// this is not an api's type, it is used in UI for filtering purposes
export const MASTER_NETWORK = 'master.network';

export const AccountStatus = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
} as const;
export type AccountStatus = ConstValues<typeof AccountStatus>;

export const AccountExperience = {
  NETWORK_DISPLAY: 'network.display',
  NETWORK_MOBILE: 'network.mobile',
  ADVERTISER_EXCHANGE: 'advertiser.exchange',
  ADVERTISER: 'advertiser',
  PUBLISHER: 'publisher',
  AGENCY: 'agency',
  PUBLISHER_SELF_SERVE: 'publisher.self_serve',
} as const;
export type AccountExperience = ConstValues<typeof AccountExperience>;

export const AccountDealPricingRuleType = {
  BANNER: 'Banner',
  CTV: 'CTV',
  VIDEO: 'Video',
} as const;
export type AccountDealPricingRuleTypeValues = ConstValues<typeof AccountDealPricingRuleType>;

export type AccountDealPricingRule = {
  ad_category: string | null;
  deal_rule_type: AccountDealPricingRuleTypeValues;
  price: string;
};

export interface Product {
  family: string;
  name: string;
  product: string;
  salesforce_id: string;
  status: string;
}

export interface AccountingInformation {
  city: string;
  country: string;
  salesforce_address_type: string;
  state: string;
  street: string;
  zip: string;
}

export type AccountPaymentsHistory = {
  amount: string;
  created_date: string;
  creator_email: string;
  creator_id: string;
  creator_uid: string;
  id: string;
  modified_date: string;
  notes: string;
};

export type AccountExchange = {
  blocked_adcategories?: Record<string, boolean>;
  blocked_adcategories_iab_v2?: Record<string, boolean>;
  domains?: Record<string, boolean>;
  domain_targeting_option?: 'ROOT' | 'SUBDOMAIN';
  ssrtb: {
    encryption_key?: string;
    integrity_key?: string;
    is_map_allowed?: boolean;
    preferred_taxonomy: string;
    metrics?: Record<string, boolean>;
    enrichment_features?: Record<string, boolean>;
    header_object?: boolean;
    tid_enabled?: boolean;
    send_floors?: boolean;
    lurl_enabled?: boolean;
    send_multiformat_requests?: boolean;
    indium_chloride_partner_id?: string;
    compression?: RtbEndpointCompression;
    exclude_mfa?: boolean;
  } | null;
  traffic_filter_eids?: Record<number, boolean>;
  skadnetwork_ids?: Record<string, boolean>;
  monthly_budget: string;
  prepaid_initial_budget: string;
  prepaid_budget_description: string;
  flags?: Record<string, boolean>;
  multi_imp_structured_ad_pods?: boolean;
  multi_imp_dynamic_ad_pods?: boolean;
};

export type Account = {
  accept_bid_below_floor: boolean;
  account_id: string | null;
  account_uid?: string | null;
  accounting_info: AccountingInformation[];
  acl?: AccEditableAcls;
  acl_override?: Record<string, boolean> | null;
  compiled_acl: string;
  contact_email?: string;
  contact_name?: string;
  contextual_providers: { [key: string]: true } | null;
  country_of_business: string;
  created_date: string;
  currency: string;
  deal_custom_pricing_rules: AccountDealPricingRule[];
  default_cpm_cap?: null | string;
  default_fees_description?: null | string;
  default_platform_share_ox_sourced?: null | string;
  default_platform_share_partner_sourced?: null | string;
  default_share?: null | string;
  demand_legal_entity?: string;
  demand_oracle_info: {
    oracle_id: string | null;
    oracle_name: string | null;
  };
  dmp_sources?: string | null;
  exchange?: AccountExchange | null;
  exchange_account_network?: { [key: string]: object | undefined } | null;
  exchange_account_network_currency?: string | null;
  experience: AccountExperience;
  external_id?: string | null;
  gross_bidding?: boolean;
  has_exchange?: boolean;
  has_ssrtb?: boolean;
  iab_tcf_global_vendor_list_id: string;
  id: string;
  instance_uid: string;
  max_cpm_cap?: null | string;
  max_share?: null | string;
  modified_date: string;
  name: string;
  notes: string;
  organization_type?: string | null;
  partner_id: string | null;
  partner_type: string[];
  partner_uid: string | null;
  products?: Product[];
  pub_lowest_hard_floor_multiplier?: string | null;
  /** Controls whether the Publisher contributes to hard Post Auction Discounts and Rebates or not. */
  publisher_contribution?: boolean | null;
  reporting_api_enabled?: boolean;
  reporting_tc_agreement: ReportingTcAgreement;
  sellers_json: SellerJsonOverride;
  status: AccountStatus;
  supply_oracle_info: {
    oracle_id: string | null;
    oracle_name: string | null;
  };
  third_party_partners: string[] | null;
  timezone?: string | null;
  timezone_id?: string | null;
  type: typeof ObjectType.Account;
  type_full: AccountType;
  uid: string;
};

export type ReportingTcAgreement =
  | {
      flag: false;
      email: null;
      sign_date: null;
      user_id: null;
    }
  | {
      flag: true;
      email: string;
      sign_date: string;
      user_id: NumberString;
    };

export type AccountTableData = {
  id: string;
  name: string;
  status: AccountStatus;
  account_type: string;
  managing_account: string;
};

export type EnrichedAccount = {
  market: Market | null;
} & Account;

export type AccEditableAcls = {
  [ACL_TYPE.DEAL_READ]?: boolean;
  [ACL_TYPE.DMP_SOURCES_READ]?: boolean;
  [ACL_TYPE.EXT_INVENTORY_MAP_READ]?: boolean;
  [ACL_TYPE.FLOORRULE_READ]?: boolean;
  [ACL_TYPE.LOOKALIKE_SEGMENTS_ENABLED]?: boolean;
  [ACL_TYPE.RESULTS_ENABLED]?: boolean;
  [ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_READ]?: boolean;
  [ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_WRITE]?: boolean;
  [ACL_TYPE.THIRD_PARTY_FEES_READ]?: boolean;
  [ACL_TYPE.THIRD_PARTY_FEES_WRITE]?: boolean;
};

export type AccountACL = {
  acl?: AccEditableAcls;
};

export type AccountSimplified = AccountACL &
  Pick<
    Account,
    | 'id'
    | 'uid'
    | 'name'
    | 'type_full'
    | 'status'
    | 'account_id'
    | 'account_uid'
    | 'type'
    | 'instance_uid'
    | 'dmp_sources'
    | 'modified_date'
  > &
  Partial<Account>;

export interface AccountTree extends AccountSimplified {
  expanded?: boolean;
  children?: AccountTree[];
  depth?: number;
  customRowStyle?: Record<string, string>;
  parentName?: string;
  isFetching?: boolean;
  showArrowIcon?: boolean;
}

export type AccountsMap = Record<string, Account>;

export const SellerType = {
  RESELLER: 'RESELLER',
  DIRECT: 'DIRECT',
} as const;
export type SellerType = ConstValues<typeof SellerType>;

export const AccountsChipFilters = {
  type_full: 'type_full',
  status: 'status',
  account_uid: 'account_uid',
  instance_uid: 'instance_uid__in',
  experience: 'experience',
} as const;
export type AccountsChipFilters = ConstValues<typeof AccountsChipFilters>;

export const RtbEndpointCompression = {
  GZIP: 'gzip',
  NONE: 'none',
} as const;
export type RtbEndpointCompression = ConstValues<typeof RtbEndpointCompression>;

export type RtbEndpointApi = {
  account_uid: string;
  compression: RtbEndpointCompression;
  name: string;
  uid?: string;
  url: string;
  id: string;
};

export type LineItemEndpoint = {
  endpoint_id: string;
  colo: string;
  qps: string | number | null;
};

export type Timezone = {
  legacy_id: number;
  id: string;
  name: string;
};

export type ContextualProvider = {
  name: string;
  id: string;
};
